<script setup lang="ts">
    import DebugLog from "@/components/DebugLog/DebugLog.vue";
    import KlinikenHeader from "@/components/KlinikenHeader.vue";
    import KlinikenMenu from "@/components/KlinikenMenu.vue";
    import ModalIdle from "@/components/ModalIdle.vue";
    import useHandleEntryParameters from "@/composables/useHandleEntryParameters";
    import { useStore } from "@/store";
    import TabContainer from "@/tabs/TabContainer.vue";
    import { openDialog } from "@/utils";
    import throttle from "lodash/throttle";
    import { computed, onBeforeMount, onMounted, onUnmounted, ref, watch } from "vue";

    const { handleEntryParameters } = useHandleEntryParameters();
    const store = useStore();

    const showMenu = computed<boolean>(() => store.getters["loginMode/showMenu"]);
    const enableIdle = computed<boolean>(() => store.getters["idle/enableIdle"]);
    const maxIdle = computed<number>(() => store.getters["idle/maxIdle"]);
    const countdownTime = computed<number>(() => store.getters["idle/countdownTime"]);
    const lastActive = computed<Date>(() => store.getters["idle/lastActive"]);

    const isIdle = ref<boolean>(false);
    const idleWorker = ref<null | ServiceWorkerRegistration>(null);
    const minimized = ref<boolean>(false);

    const createIdleWorker = async () => {
        if (enableIdle.value) {
            try {
                idleWorker.value = await navigator.serviceWorker.register("idleWorker.js", {
                    scope: "./",
                });

                navigator.serviceWorker.addEventListener(
                    "message",
                    (
                        event: MessageEvent<{
                            isIdle: boolean;
                            logout?: boolean;
                        }>
                    ) => {
                        isIdle.value = event.data.isIdle;

                        if (event.data.logout) {
                            logout();
                        }
                    }
                );
                idleWorker.value?.active?.postMessage({
                    maxIdle: maxIdle.value,
                    countdownTime: countdownTime.value,
                });
            } catch (error) {
                console.error(`Registration failed with ${error}`);
            }
        }
    };

    const toggleSize = (minimizedValue: boolean) => {
        minimized.value = minimizedValue;
    };

    const checkIdle = throttle(
        () => {
            const now = new Date();

            if (enableIdle.value) {
                idleWorker.value?.active?.postMessage({ reset: true });
                isIdle.value = false;

                if (now.getTime() - lastActive.value.getTime() > maxIdle.value + countdownTime.value) {
                    logout();
                }
            }

            store.commit("idle/setLastActive", now);
        },
        1000,
        { leading: true, trailing: false }
    );

    const logout = () => {
        store.dispatch("userData/logout");
    };

    // Startas här (inte i onMounted) för att inte fördröja hämtningen i onödan, men avsiktligt inte
    // await:ad förrän i onMounted - se kommentaren vid await entryParametersPromise nedan.
    const entryParametersPromise = handleEntryParameters();

    onBeforeMount(async () => {
        /**
         * Sätter inställningar för automatiska utloggningen
         */
        store.dispatch("idle/configure");
    });

    onMounted(async () => {
        if ("serviceWorker" in navigator) {
            await createIdleWorker(); // create worker

            watch(maxIdle, (newValue) => {
                if (enableIdle.value) {
                    idleWorker.value?.active?.postMessage({
                        maxIdle: maxIdle.value,
                    });
                }
            });
        }

        window.addEventListener("click", checkIdle);
        window.addEventListener("mousemove", checkIdle);
        window.addEventListener("keypress", checkIdle);
        window.addEventListener("scroll", checkIdle);
        window.addEventListener("focus", checkIdle);
        window.addEventListener("touchstart", checkIdle);
        window.addEventListener("touchmove", checkIdle);
        window.addEventListener("touchend", checkIdle);
        window.addEventListener("touchcancel", checkIdle);
        window.addEventListener("touchleave", checkIdle);
        window.addEventListener("touchenter", checkIdle);

        /**
         * Väntar in att t.ex. ?patient= i URL:en (se useHandleEntryParameters.js) hunnit sätta
         * patientData.currentId innan launchView öppnar en patientbunden vy - annars hinner tabben aldrig
         * öppnas (se tabList.js::openTab) om anropet mot backend inte hunnit svara än.
         */
        await entryParametersPromise;
        await store.dispatch("loginMode/launchView");
        store.dispatch("triggers/startOnDayChange");
    });

    onUnmounted(() => {
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.getRegistrations().then((registrations) => {
                for (const registration of registrations) {
                    registration.unregister();
                }
            });
        }

        window.removeEventListener("click", checkIdle);
        window.removeEventListener("mousemove", checkIdle);
        window.removeEventListener("keypress", checkIdle);
        window.removeEventListener("scroll", checkIdle);
        window.removeEventListener("focus", checkIdle);
        window.removeEventListener("touchstart", checkIdle);
        window.removeEventListener("touchmove", checkIdle);
        window.removeEventListener("touchend", checkIdle);
        window.removeEventListener("touchcancel", checkIdle);
        window.removeEventListener("touchleave", checkIdle);
        window.removeEventListener("touchenter", checkIdle);

        store.dispatch("triggers/stopOnDayChange");
    });
</script>

<template>
    <div id="kliniken-frame" :class="{ minimized: minimized }">
        <KlinikenHeader :minimized="minimized" />
        <KlinikenMenu v-if="showMenu" @toggleSize="toggleSize" />
        <div :class="{ content: showMenu }">
            <TabContainer />
        </div>
        <ModalIdle
            v-if="enableIdle && isIdle"
            :maxIdleFor="maxIdle"
            :countdownTime="countdownTime"
            @close="checkIdle"
        />
        <DebugLog />
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";

    #kliniken-frame {
        height: 100dvh;

        #kliniken-menu {
            height: 100%;
            width: $menu-maximized-width;
            position: fixed;
            z-index: 1;
            top: $header-height;
            left: 0;
        }

        .content {
            margin-left: $menu-maximized-width;
        }

        &.minimized {
            #kliniken-menu {
                width: $menu-minimized-width;
            }

            .content {
                margin-left: $menu-minimized-width;
            }
        }

        main {
            height: 100dvh;
            flex: 1 1 auto;
        }
    }
</style>
